//@flow
import React, {useState} from 'react';
import {
  FormControlLabel,
  makeStyles,
  Typography,
  Hidden,
} from '@material-ui/core';
import {Delete} from '@material-ui/icons';

import {colors} from '../../../../../../assets/styles/colors';

import type {PopupBanner} from '../../../../../../entities';
import {getImageUrl} from '../../../../../../utils/image_provider';
import ConfirmModal from '../../../../../../elements/Modals/ConfirmModal';
import IosSwitch from '../../../../../../elements/IosSwitch/IosSwitch';

type Props = {
  banner: PopupBanner,
  featureDisabled?: boolean,
  onPreview: () => void,
  onEdit?: () => void,
  onDelete: () => void,
  onToggleEnabled: () => void,
};

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    gap: '.5rem',
    position: 'relative',
    cursor: 'pointer',
    padding: '.5rem',
    borderRadius: '.75rem',
    background: colors.white,
    transition: 'opacity .2s ease-in-out',
    flexDirection: 'column',
    justifyContent: 'space-between',

    '&[data-disabled="true"]': {
      opacity: '.5',
    },

    '&:hover': {
      background: `${colors.grey200}`,
      opacity: '1',
    },

    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  thumbnail: {
    width: '80px',
    height: '80px',
    borderRadius: '.75rem',
    overflow: 'hidden',

    '& img': {
      height: '200%',
      width: '100%',
      clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)',
      objectFit: 'cover',
      borderRadius: 'inherit',
    },
  },
  data: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    flexGrow: 1,
  },
  title: {
    fontSize: '14px',
    fontWeight: '500',
  },
  subtitle: {
    fontSize: '14px',
  },
  subtitleDisabled: {
    color: colors.warning,
    fontWeight: 600,
    fontSize: '14px',
  },

  actions: {
    display: 'flex',
    padding: '.25rem',
    height: '80px',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    cursor: 'auto',
  },
  enableSwitch: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    margin: 0,

    '& .label': {
      fontSize: '14px',
    },

    '& span': {
      margin: 0,
    },
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '.25rem',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    padding: '.25rem',
    borderRadius: '99px',

    '&:hover': {
      '&[data-action="edit"]': {
        background: `${colors.primary}1c`,
      },

      '&[data-action="delete"]': {
        background: `${colors.error}1c`,
      },
    },
  },
  deleteIconButton: {
    color: colors.error,
    background: `${colors.error}26`,
    borderRadius: '50%',
    padding: '.25rem',
  },
  editLabelButton: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: colors.primary,
  },
  deleteLabelButton: {
    fontSize: '14px',
    fontWeight: '500',
  },
  switchLabel: {
    fontSize: '14px',
  },
  mobileActions: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '.5rem',
    width: '100%',
    flexDirection: 'column',

    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
  },
  itemData: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
  },
}));

export default function PopupBannerItem(props: Props) {
  const {banner, onDelete, featureDisabled, onPreview, onToggleEnabled} = props;

  const classes = useStyles();

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const openConfirmModal = () => setConfirmModalOpen(true);
  const closeConfirmModal = () => setConfirmModalOpen(false);

  const {imageId, title, subtitle, enabled} = banner;

  const image = imageId && getImageUrl(imageId);

  return (
    <>
      <div
        className={classes.container}
        role="button"
        onClick={() => {
          onPreview();
        }}
        data-disabled={!enabled}
      >
        <div className={classes.itemData}>
          <picture
            className={classes.thumbnail}
            style={{
              background: banner.backgroundColor,
            }}
          >
            {image && (
              <img src={image} alt={`Banner que lleva como titulo ${title}`} />
            )}
          </picture>

          <div className={classes.data}>
            <Typography className={classes.title}>{title}</Typography>
            <Typography className={classes.subtitle}>{subtitle}</Typography>
            {banner.buttonText && (
              <Typography className={classes.subtitle}>
                Botón: {banner.buttonText}
              </Typography>
            )}
            {featureDisabled && (
              <Typography className={classes.subtitleDisabled}>
                ⚠ El banner no se muestra en la tienda
              </Typography>
            )}
          </div>
        </div>

        <Hidden smDown>
          <footer
            onClick={e => {
              e.stopPropagation();
            }}
            className={classes.actions}
          >
            <FormControlLabel
              labelPlacement="start"
              control={
                <div className={classes.switch}>
                  <IosSwitch checked={enabled} onChange={onToggleEnabled} />
                </div>
              }
              classes={{
                label: classes.switchLabel,
              }}
              label={enabled ? 'Habilitado' : 'Deshabilitado'}
            />
            <button
              className={classes.actionButton}
              data-action="delete"
              onClick={e => {
                e.stopPropagation();
                openConfirmModal();
              }}
            >
              <span className={classes.deleteLabelButton}>Eliminar</span>
              <Delete className={classes.deleteIconButton} />
            </button>
          </footer>
        </Hidden>
        <Hidden mdUp>
          <footer
            onClick={e => {
              e.stopPropagation();
            }}
            className={classes.mobileActions}
          >
            <FormControlLabel
              labelPlacement="end"
              control={
                <div className={classes.switch}>
                  <IosSwitch checked={enabled} onChange={onToggleEnabled} />
                </div>
              }
              className={classes.enableSwitch}
              classes={{
                label: classes.switchLabel,
              }}
              label={enabled ? 'Habilitado' : 'Deshabilitado'}
            />
            <button
              className={classes.actionButton}
              data-action="delete"
              onClick={e => {
                e.stopPropagation();
                openConfirmModal();
              }}
            >
              <Delete className={classes.deleteIconButton} />
              <span className={classes.deleteLabelButton}>Eliminar</span>
            </button>
          </footer>
        </Hidden>
      </div>

      <ConfirmModal
        open={confirmModalOpen}
        title="¿Estás seguro de eliminar este banner?"
        message="Esta acción no se puede deshacer"
        onConfirm={onDelete}
        onClose={() => closeConfirmModal()}
        onCancel={() => closeConfirmModal()}
      />
    </>
  );
}
